import type { ProductLine, SubscriptionInfo } from '@/graphql/graphql';

const PRODUCT_RANK: Record<ProductLine, number> = { VIEWER: 0, PRO: 1 };
export const productAtLeast = (current: ProductLine, required: ProductLine): boolean =>
  PRODUCT_RANK[current] >= PRODUCT_RANK[required];

// NONE is the FREE plan with no Stripe subscription (where an org lands when
// its trial ends) — a usable plan, so it is not voiding. Mirror of
// VOIDING_BILLING_STATUSES in libs/common/src/tier.ts.
const VOIDING_BILLING_STATUSES = ['CANCELED', 'UNPAID'];

export interface FeatureGate {
  minProduct?: ProductLine;
  minStorageBytes?: number;
}

export type FeatureKey =
  | 'viewer.clipBox'
  | 'viewer.exportClipBox'
  | 'viewer.elevationProfile'
  | 'viewer.stockpile'
  | 'viewer.stockpileFromSurface'
  | 'viewer.pipe'
  | 'viewer.addLine'
  | 'viewer.addPolygon'
  | 'viewer.alignmentWalk'
  | 'sharing.publicLink'
  | 'organization.authBranding'
  | 'organization.customDomain'
  | 'processing.generateRcs'
  | 'analysis.rainfall'
  | 'analysis.deviation'
  | 'analysis.surfaceDifference'
  | 'analysis.stationVolumes'
  | 'analysis.surfaceExpectation'
  | 'analysis.layerStack'
  | 'analysis.clearance'
  | 'analysis.crestLine'
  | 'analysis.groundClassify'
  | 'analysis.meshExport'
  | 'export.vectorBackend'
  | 'export.landxml'
  | 'report.pdf';

// TODO(owner): finalize each gate. Placeholder = available on any Viewer plan.
// Examples of the two axes:
//   { minStorageBytes: 500_000_000_000 }  → requires Team (500 GB) or larger
//   { minProduct: 'PRO' }                 → Professional only
export const FEATURE_GATES: Record<FeatureKey, FeatureGate> = {
  'viewer.clipBox': {},
  'viewer.exportClipBox': { minProduct: 'PRO' },
  'viewer.elevationProfile': {},
  'viewer.stockpile': {},
  // Convenience path: seed the stockpile compute from an already existing
  // surface/polygon (layer context menu + feature drawer). Drawing a fresh
  // stockpile stays on the base 'viewer.stockpile' gate.
  'viewer.stockpileFromSurface': { minProduct: 'PRO' },
  'viewer.pipe': { minProduct: 'PRO' },
  'viewer.addLine': {},
  'viewer.addPolygon': {},
  // Walking an alignment (station stepper toolbar + camera follow). The
  // walk can only be started from the LandXML / feature property drawers,
  // and the toolbar hotkeys are enabled only while a walk is active, so
  // gating those two entry handlers covers the hotkeys too.
  'viewer.alignmentWalk': { minProduct: 'PRO' },
  'sharing.publicLink': {},
  'organization.authBranding': {},
  'organization.customDomain': { minProduct: 'PRO' },
  'processing.generateRcs': { minProduct: 'PRO' },
  'analysis.rainfall': { minProduct: 'PRO' },
  'analysis.deviation': { minProduct: 'PRO' },
  'analysis.surfaceDifference': { minProduct: 'PRO' },
  // Station-by-station breakdown of a surface difference (the dock's Section
  // tab, the 3D corridor bands, the dialog's station section and the dock's
  // bin-stepping hotkeys). Same tier as the analysis it refines.
  'analysis.stationVolumes': { minProduct: 'PRO' },
  // Thickness and conformance verification: the expectation block on a
  // surface-difference or deviation run (design thickness, tolerances, the
  // verdict and its criteria). Same tier as the analyses it refines.
  'analysis.surfaceExpectation': { minProduct: 'PRO' },
  // The multi-layer thickness analysis: N construction LEVELS giving N-1
  // LAYERS, each verified against its design thickness. Same tier as the
  // single-pair analyses it generalises.
  'analysis.layerStack': { minProduct: 'PRO' },
  // Sweeping a clearance profile along an alignment and classifying the
  // intruding points (ruling R5 — same class as the other analyses).
  'analysis.clearance': { minProduct: 'PRO' },
  // Crest-line extraction from a design surface. The gate covers all four
  // entry points: the layer menu item, the dialog, the draw-guide picking
  // tool and the live preview — the preview is the feature's actual value, so
  // gating only the menu item would leave it open.
  'analysis.crestLine': { minProduct: 'PRO' },
  'analysis.groundClassify': { minProduct: 'PRO' },
  'analysis.meshExport': { minProduct: 'PRO' },
  'export.vectorBackend': {},
  'export.landxml': { minProduct: 'PRO' },
  'report.pdf': { minProduct: 'PRO' },
};

const WHITELISTED_EMAIL_DOMAINS = ['actegon.com', 'skygis.cloud'];
export const isWhitelistedEmail = (email: string | null | undefined): boolean => {
  if (!email) return false;
  const at = email.lastIndexOf('@');
  if (at < 0) return false;
  return WHITELISTED_EMAIL_DOMAINS.includes(email.slice(at + 1).toLowerCase());
};

// Per-feature organization overrides. Organizations listed here bypass the
// normal subscription gate for the specified feature.
const WHITELISTED_ORGANIZATIONS: Partial<Record<FeatureKey, string[]>> = {
  'processing.generateRcs': ['faab642c-0474-4fba-91b4-a261a03f2dc2'], // topocrew
};

export const isFeatureAllowed = (
  sub: SubscriptionInfo,
  email: string | null | undefined,
  key: FeatureKey,
  organizationId?: string | null,
): boolean => {
  if (isWhitelistedEmail(email)) return true;
  if (organizationId && WHITELISTED_ORGANIZATIONS[key]?.includes(organizationId)) return true;
  if (VOIDING_BILLING_STATUSES.includes(sub.billingStatus)) return false;
  const gate = FEATURE_GATES[key];
  if (gate.minProduct && !productAtLeast(sub.productLine, gate.minProduct)) return false;
  if (gate.minStorageBytes != null && sub.storageLimitBytes < gate.minStorageBytes) return false;
  return true;
};
